// src/pages/MyAccount/MyProfile.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Pencil, Trash2, Save, X, CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import ChangePasswordModal from "./ChangePasswordModal";
import { api } from "../../services/apiService";
import { clearAuthData } from "../../utils/tokenUtils";
import {
  getInitials,
  toTitleCase,
  formatDate,
  formatDateTime,
} from "../../utils/formatters";
import { toast } from "react-hot-toast";
import { Input } from "../../components/ui/input";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import FormSkeleton from "../../components/skeletons/FormSkeleton";
import { cn } from "../../lib/utils";

const DenseTable = ({ rows, className }) => (
  <div
    className={cn(
      "grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 sm:gap-x-8",
      "lg:grid-cols-3",
      className,
    )}
  >
    {rows.map((r, idx) => (
      <div key={idx} className="flex min-w-0 flex-col gap-1">
        <span className="text-sm text-text-secondary font-semibold">
          {r.label}
        </span>
        <span className="text-base text-text-primary break-words min-w-0">
          {r.value ?? "—"}
        </span>
      </div>
    ))}
  </div>
);

const extractPayload = (response) => {
  if (!response) return null;
  if (response.data && typeof response.data === "object") return response.data;
  if (response.success !== undefined || response.data !== undefined)
    return response;
  return null;
};

const MyProfile = () => {
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoActionBusy, setPhotoActionBusy] = useState(false);
  const lastPreviewObjectUrl = useRef(null);
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [rules, setRules] = useState({
    minLength: false,
    uppercase: false,
    number: false,
    symbol: false,
  });
  const [serverError, setServerError] = useState("");

  const [strength, setStrength] = useState(0);

  const computeStrength = (pw) => {
    if (!pw) return 0;
    const lengthScore = Math.min(16, pw.length) / 16;
    const hasUpper = /[A-Z]/.test(pw);
    const hasLower = /[a-z]/.test(pw);
    const hasNum = /[0-9]/.test(pw);
    const hasSym = /[^A-Za-z0-9]/.test(pw);
    const variety =
      [hasUpper, hasLower, hasNum, hasSym].filter(Boolean).length / 4;
    return Math.round((lengthScore * 0.4 + variety * 0.6) * 100);
  };

  useEffect(() => {
    const pw = form.new_password || "";
    setRules({
      minLength: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      number: /[0-9]/.test(pw),
      symbol: /[^A-Za-z0-9]/.test(pw),
    });
    setStrength(computeStrength(pw));
  }, [form.new_password]);

  const isSuccessPayload = (payload) => {
    if (payload == null) return false;
    if (typeof payload !== "object") return false;
    if (payload.success === false) return false;
    if (payload.success === true || payload.success === "true") return true;
    const msg = String(
      payload.message || payload.data?.message || "",
    ).toLowerCase();
    if (msg.includes("password changed") || msg.includes("password updated"))
      return true;
    return false;
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();

    setLoading(true);
    setServerError("");

    try {
      const response = await api.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
        confirm_password: form.confirm_password,
      });

      const payload = extractPayload(response) ?? response;
      if (!isSuccessPayload(payload)) {
        const hint =
          (typeof payload?.message === "string" && payload.message) ||
          "Failed to change password";
        throw new Error(hint);
      }

      try {
        await api.logout();
      } catch (logoutErr) {
        console.warn(
          "Logout after password change failed; continuing",
          logoutErr,
        );
      }

      clearAuthData();
      sessionStorage.clear();

      setForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      setConfirmModalOpen(false);
      setChangePasswordOpen(false);
      setCountdown(5);
      setSuccessDialogOpen(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to change password. Try again.";
      setServerError(msg);
      toast.error(msg);
      setConfirmModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const logoutInProgressRef = useRef(false);
  const handleLogout = (logout = false) => {
    if (logout) {
      clearAuthData();
      sessionStorage.clear();
    }
  };
  // fetchProfile is stable and used by event listener
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getUserProfile();
      const payload = extractPayload(response);
      if (!payload) {
        setError("Failed to load profile data (no payload)");
        setUser(null);
        return;
      }
      const data =
        payload.success === true && payload.data
          ? payload.data
          : payload.user_id
            ? payload
            : null;
      if (!data) {
        setError("Failed to load profile data");
        setUser(null);
        return;
      }
      setUser(data);
    } catch (err) {
      console.error("Profile fetch error:", err);
      setError("Something went wrong while fetching profile");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClose = () => setEditProfileOpen(false);

  const handleLoginAgain = useCallback(async () => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    try {
      const token =
        localStorage.getItem("authorization") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        localStorage.getItem("authToken");
      if (token) await api.logout();
    } catch (err) {
      console.warn("Logout API failed; clearing locally", err);
    }

    clearAuthData();
    setSuccessDialogOpen(false);
    window.location.replace("/login");
  }, []);

  useEffect(() => {
    if (!successDialogOpen) return;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          handleLoginAgain();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [successDialogOpen, handleLoginAgain]);

  const isUpdateSuccess = (response) =>
    response?.success === true ||
    response?.success === "true" ||
    response?.updated === true ||
    response?.status === "ok" ||
    response?.status === "success" ||
    (response?.data &&
      typeof response.data === "object" &&
      response.data.user_id);

  const handleSave = async () => {
    if (!user?.user_id) {
      toast.error("User ID not found");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await api.updateUser(user.user_id, {
        full_name: name,
        email,
        mobile_number: mobileNumber || null,
      });

      if (!isUpdateSuccess(response)) {
        const msg =
          response?.message || response?.error || "Failed to update profile";
        throw new Error(msg);
      }

      try {
        window.dispatchEvent(new CustomEvent("profileUpdated"));
      } catch (_) {
        /* ignore */
      }

      toast.success("Profile updated successfully");
      handleClose();
    } catch (err) {
      const msg = (err && err.message) || "Failed to update profile";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadProfilePhoto = async (file) => {
    if (!user?.user_id) {
      toast.error("User ID not found");
      return;
    }

    if (lastPreviewObjectUrl.current) {
      URL.revokeObjectURL(lastPreviewObjectUrl.current);
      lastPreviewObjectUrl.current = null;
    }
    const objectUrl = URL.createObjectURL(file);
    lastPreviewObjectUrl.current = objectUrl;
    setPhotoPreview(objectUrl);
    setUploadProgress(0);

    try {
      const fd = new FormData();
      fd.append("profile_image", file);
      fd.append("full_name", user.full_name);
      fd.append("email", user.email);
      fd.append("mobile_number", user.mobile_number || "");

      const response = await api.updateUser(user.user_id, fd, {
        onUploadProgress: (progressEvent) => {
          if (!progressEvent) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percent);
        },
      });

      if (!isUpdateSuccess(response)) {
        const msg =
          response?.message || response?.error || "Failed to update photo";
        throw new Error(msg);
      }

      if (lastPreviewObjectUrl.current) {
        URL.revokeObjectURL(lastPreviewObjectUrl.current);
        lastPreviewObjectUrl.current = null;
      }
      setPhotoPreview("");

      try {
        window.dispatchEvent(new CustomEvent("profileUpdated"));
      } catch (_) {
        /* ignore */
      }

      toast.success("Profile photo updated");
    } catch (err) {
      if (lastPreviewObjectUrl.current) {
        URL.revokeObjectURL(lastPreviewObjectUrl.current);
        lastPreviewObjectUrl.current = null;
      }
      setPhotoPreview("");
      const msg = (err && err.message) || "Failed to update photo";
      toast.error(msg);
      try {
        await fetchProfile();
      } catch (_) {
        /* ignore */
      }
    } finally {
      setTimeout(() => setUploadProgress(0), 800);
    }
  };

  const handleRemoveProfilePhoto = async () => {
    if (!user?.user_id) return;
    if (!user.profile_image && !photoPreview) {
      return;
    }

    setPhotoActionBusy(true);
    try {
      if (lastPreviewObjectUrl.current) {
        URL.revokeObjectURL(lastPreviewObjectUrl.current);
        lastPreviewObjectUrl.current = null;
      }
      setPhotoPreview("");

      const response = await api.updateUser(user.user_id, {
        full_name: user.full_name,
        email: user.email,
        mobile_number: user.mobile_number || null,
        remove_photo: true,
      });

      if (!isUpdateSuccess(response)) {
        const msg =
          response?.message || response?.error || "Failed to remove photo";
        throw new Error(msg);
      }

      try {
        window.dispatchEvent(new CustomEvent("profileUpdated"));
      } catch (_) {
        /* ignore */
      }

      toast.success("Profile photo removed");
    } catch (err) {
      const msg = (err && err.message) || "Failed to remove photo";
      toast.error(msg);
      try {
        await fetchProfile();
      } catch (_) {
        /* ignore */
      }
    } finally {
      setPhotoActionBusy(false);
    }
  };

  // mount: fetch once, and add event listener that triggers a refetch
  useEffect(() => {
    fetchProfile();

    const onProfileUpdated = () => {
      // refetch authoritative data from server
      fetchProfile();
    };
    window.addEventListener("profileUpdated", onProfileUpdated);

    return () => {
      window.removeEventListener("profileUpdated", onProfileUpdated);
    };
  }, [fetchProfile]);
  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Only JPEG, JPG, and PNG images are allowed.",
      );
      e.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(
        "File size exceeds 5MB limit. Please choose a smaller image.",
      );
      e.target.value = "";
      return;
    }

    e.target.value = "";
    void handleUploadProfilePhoto(file);
  };

  if (loading) {
    return (
      <div className="flex flex-1 w-full min-h-0 items-start justify-stretch bg-bg-secondary p-4 sm:p-6">
        <div className="w-full min-w-0">
          <FormSkeleton fields={6} className="max-w-none mx-0" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 w-full min-h-0 items-center justify-center bg-bg-secondary">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!user) return null;

  const joinedDate = user.created_at ? formatDate(user.created_at) : "—";
  const lastLogin =
    user.updated_at || user.last_login
      ? formatDateTime(user.updated_at || user.last_login)
      : "—";

  const roleDisplay =
    user.role_name || user.role
      ? toTitleCase(String(user.role_name || user.role).replace(/[_-]/g, " "))
      : "—";

  const accountRows = [
    { label: "Role", value: roleDisplay },
    { label: "Department", value: user.department ?? "—" },
    { label: "Status", value: user.is_active ? "Active" : "Inactive" },
    { label: "Member Since", value: joinedDate },
    { label: "Last Login", value: lastLogin },
    { label: "Verified", value: user.is_verified ? "Yes" : "No" },
  ];

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-bg-secondary">
      <div className="flex w-full min-w-0 flex-col">
        <Card
          className={cn(
            "flex w-full min-w-0 flex-col rounded-none border-x-0 border-t-0 border-b",
            "bg-card shadow-lg",
            "min-h-[calc(100dvh-4.0625rem)]",
            "p-4 pb-6 sm:p-6 sm:pb-8 lg:px-8 lg:pb-10 xl:px-10 xl:pb-12",
          )}
        >
          <div className="w-full min-w-0 mb-4">
            <h1 className="font-archivo text-center text-xl leading-7 font-semibold text-[#171A1FFF] sm:text-left sm:text-2xl sm:leading-8">
              My Profile
            </h1>
          </div>

          <Separator className="w-full min-w-0 border border-[#DEE1E6]" />

          <div
            className={cn(
              "relative my-4 w-full min-w-0 max-w-full overflow-x-hidden rounded-[10px] bg-[#F2F2FDFF]",
              "flex flex-col items-center justify-center gap-4 px-4 py-6 text-center",
              "sm:px-6 sm:py-8",
              "min-h-[200px] sm:min-h-[220px] md:min-h-[240px] lg:px-8",
            )}
          >
            <div className="flex w-full min-w-0 max-w-md flex-col items-center gap-4 sm:max-w-lg">
              <div className="flex shrink-0 flex-col items-center">
                <Avatar className="h-32 w-32 shrink-0">
                  {(photoPreview || user.profile_image) && (
                    <AvatarImage
                      src={photoPreview || user.profile_image}
                      alt={user.full_name || user.email}
                    />
                  )}
                  <AvatarFallback className="bg-bg-tertiary text-text-primary text-4xl font-semibold">
                    {getInitials(user.full_name || user.email || "U")}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex w-full min-w-0 flex-col items-center gap-2">
                <div className="flex w-full max-w-full flex-col items-stretch justify-center gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3">
                  <input
                    ref={fileInputRef}
                    id="photo-upload"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    className="hidden"
                    onChange={handlePhotoChange}
                    autoComplete="off"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full shrink-0 font-bold text-primary-600 sm:w-auto"
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    disabled={saving || photoActionBusy || uploadProgress > 0}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Change Photo
                  </Button>

                  {(photoPreview || user.profile_image) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRemoveProfilePhoto}
                      className="w-full shrink-0 font-bold text-red-600 sm:w-auto"
                      type="button"
                      disabled={saving || photoActionBusy || uploadProgress > 0}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove Photo
                    </Button>
                  )}
                </div>
                <p className="mb-0 max-w-full text-center text-xs text-text-tertiary">
                  Supported formats: JPG, JPEG, PNG (Max 5MB)
                </p>
                {uploadProgress > 0 && (
                  <div className="mt-1 w-full max-w-full">
                    <div className="h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-primary-600 transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-text-secondary">
                      Uploading: {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator className="mb-6 w-full min-w-0" />

          <div className="flex min-w-0 w-full shrink-0 flex-col gap-8 pb-2 sm:pb-4">
            <div>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <h3 className="font-archivo text-lg leading-7 font-semibold text-[#636AE8] sm:text-[20px] sm:leading-[28px]">
                  Personal Information
                </h3>
                {editProfileOpen ? (
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                    <Button
                      onClick={handleClose}
                      disabled={saving}
                      className="w-full shrink-0 bg-white text-[#171A1FFF]
                      border-solid border-2 border-[#DEE1E6FF] sm:w-auto"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="w-full shrink-0 bg-white text-[#171A1FFF] border-solid border-2 border-[#DEE1E6FF] sm:w-auto"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Profile
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      setEditProfileOpen(true);
                      setName(user.full_name);
                      setEmail(user.email);
                      setMobileNumber(user.mobile_number);
                    }}
                    disabled={saving || photoActionBusy || uploadProgress > 0}
                    className="w-full shrink-0 bg-white text-[#171A1FFF] border-solid border-2 border-[#DEE1E6FF] sm:w-auto"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
              {/* <DenseTable rows={personalRows} /> */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-sm text-text-secondary font-semibold">
                    Name
                  </span>

                  {editProfileOpen ? (
                    <Input
                      value={name}
                      onChange={(e) => {
                        console.log("name", e.target.value);
                        setName(e.target.value);
                      }}
                      className="mt-1"
                    />
                  ) : (
                    <span className="text-base text-text-primary break-words min-w-0">
                      {user.full_name}
                    </span>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-sm text-text-secondary font-semibold">
                    Email
                  </span>
                  {editProfileOpen ? (
                    <Input
                      value={email}
                      onChange={(e) => {
                        console.log("email", e.target.value);
                        setEmail(e.target.value);
                      }}
                      className="mt-1"
                    />
                  ) : (
                    <span className="text-base text-text-primary break-words min-w-0">
                      {user.email}
                    </span>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-sm text-text-secondary font-semibold">
                    Mobile Number
                  </span>
                  {editProfileOpen ? (
                    <Input
                      value={mobileNumber}
                      onChange={(e) => {
                        console.log("mobile number", e.target.value);
                        setMobileNumber(e.target.value);
                      }}
                      className="mt-1"
                    />
                  ) : (
                    <span className="text-base text-text-primary break-words min-w-0">
                      {user.mobile_number}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Separator className="w-full border border-[#DEE1E6]" />
            <div>
              <h3 className="font-archivo text-lg leading-7 font-semibold text-[#636AE8] sm:text-[20px] sm:leading-[28px] mb-4">
                Profile Details
              </h3>
              <DenseTable rows={accountRows} />
            </div>
            <Button
              className="mt-2 flex h-10 w-full shrink-0 items-center justify-center self-start rounded-md bg-[#636AE8] font-inter text-sm font-medium leading-[22px] text-white sm:mt-6 sm:h-10 sm:w-[154px] sm:min-w-0"
              onClick={() => setChangePasswordOpen(true)}
            >
              Change Password
            </Button>
          </div>
        </Card>
      </div>

      <ChangePasswordModal
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        form={form}
        setForm={setForm}
        rules={rules}
        strength={strength}
        serverError={serverError}
        setServerError={setServerError}
      />
      <ConfirmDialog
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleChangePassword}
        title="Confirm Action"
        message="Are you sure you want to proceed?"
        confirmText="Confirm"
        cancelText="Cancel"
      />

      <Dialog
        open={successDialogOpen}
        onOpenChange={(next) => {
          if (next) setSuccessDialogOpen(true);
          else handleLogout(true);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleLoginAgain();
          }}
        >
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-7 w-7 text-green-600" aria-hidden />
            </div>
            <DialogTitle className="text-center">Password updated</DialogTitle>
            <DialogDescription className="text-center text-base text-text-primary pt-2 space-y-3">
              <span className="block font-medium text-text-primary">
                Your password has been changed successfully.
              </span>
              <span className="block">
                You have been logged out on this device. Sign in again with your
                new password.
              </span>
              <span className="block text-sm text-text-secondary">
                Redirecting to login in <strong>{countdown}</strong> seconds, or
                use the button below.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={handleLoginAgain}
              className="bg-primary-600 hover:bg-primary-700 min-w-[200px]"
            >
              Continue to login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfile;
